<template>
  <div class="column post-container" v-if="!isRecommendation" judge-on-boarding="step-1">
    <div class="content" :class="{selected, [postCssClassName]:true}" @keypress.enter.space="toggleModal()" @click='toggleModal()'>
      <Swoosh v-if="displaySwoosh" :swooshText="swooshText" :background="swooshBackground" :style="cssVars"></Swoosh>
      <div v-if="showMeeting">
        <meeting :attachment="firstAttachment" :isPostCover="true"></meeting>
      </div>
      <div v-if='showAttachment'>
        <div class="attachments">
          <div v-if="isFirstAttachmentVideoProcessing" class="image is-flex is-justify-content-center is-vcentered is-align-items-center">
            <span class="video-processing">
              <i class="fal fa-circle-notch fa-spin fa-xl"></i>
            </span>
          </div>
          <div v-else-if="imageURL"
               :class="{'is-whiteboard': isWhiteboard, 'is-finalistBooth': isFinalistBooth, 'gordon-and-betty-booth' : isGordanAndBetty, 'image' : !isGordanAndBetty}"
               :style="imageBackground" >
          </div>
        </div>
      </div>
      <div v-if="showTextBlock">
        <text-block :content="text" :grid="true"></text-block>
      </div>
      <div class="icon is-large" qa="attachment-icon" v-if="attachmentIcon">
        <i :class="attachmentIcon"></i>
      </div>
      <div v-if="post.postFeatures.isPrivate" v-bind:class="{'lock-icon' : attachmentIcon}" class="icon is-large">
        <i class="fa-regular fa-lock-keyhole"></i>
      </div>
      <div v-if="(isStatusReadyForReview || isStatusCompleted) && isMilestoneUser" class="status-icon is-medium" qa="post-status-icon">
        <i class="fa-solid" :class="postStatusIcon"></i>
      </div>
      <div class="icon is-large bottom" v-if="showPollAnswerSummary" judge-on-boarding="step-4">
        <i v-if="!pollUserAnswerSummary" class="fal fas-spinner"></i>
        <template v-else>{{ pollUserAnswerSummary }}</template>
      </div>
    </div>
  </div>
</template>

<script>
import imageProxy from '@/libs/image-proxy'
import fileType from '@/libs/file-type'
import TextBlock from '@/components/Controls/TextBlock'
import judgeOnboardingSteps from '@/mixins/judge-onboarding-steps'
import Swoosh from '@/components/SectionProjectView/Swoosh'
import attachmentTypes from '@/mixins/attachment-types'
import meeting from '@/components/Forms/AttachmentsCarousel/Attachment/Meeting'
import postStatusTypes from '@/mixins/post-status-types'
import userTypes from '@/mixins/user-types'
import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import state from '../state'

export default {
  mixins: [judgeOnboardingSteps, attachmentTypes, postStatusTypes, userTypes, auth, componentState],
  props: ['post', 'selected', 'preloadForOnboarding'],

  components: {
    TextBlock,
    meeting,
    Swoosh
  },
  computed: {
    postCssClassName: function () {
      return 'content-' + this.post.postFeatures?.name?.replace(/\s+/g, '-').toLowerCase()
    },
    displaySwoosh: function () {
      return this.post.project?.displaySwoosh || false
    },
    swooshText: function () {
      return this.post.postFeatures?.name || ''
    },
    swooshBackground: function () {
      return this.post.project?.swooshBackground || ''
    },
    projectRole: function () {
      return this._get(state.project.role, 'none')
    },
    isMilestoneUser: function () {
      return this.isAdmin || this.projectRole === 'owner' || this.projectRole === 'co-owner' || this.projectRole === 'contributor' || this.projectRole === 'viewer' || this.projectRole === 'admin' || this.projectRole === 'anonAdmin' || this.projectRole === 'anonCoordinator' || this.currentUser.type?.includes(this.UserTypeMentor) || this.currentUser.type?.includes(this.UserTypeJacAdmin)
    },
    isStatusReadyForReview: function () {
      return this.checkPostStatus(this.post) === 'ReadyForReview'
    },
    isStatusCompleted: function () {
      return this.checkPostStatus(this.post) === 'Completed'
    },
    postStatusIcon: function () {
      if (this.isStatusReadyForReview) {
        return 'fa-pen review-icon'
      } else if (this.isStatusCompleted) {
        return 'fa-circle-check completed-icon'
      } else {
        return ''
      }
    },
    hasAttachments: function () {
      return this.post.attachments.length > 0
    },
    isFinalistBooth: function () {
      return this.$route.name === 'iseffinalistbooth'
    },
    tourStatus: function () {
      return this.$tours.onboarding && this.$tours.onboarding._data.currentStep
    },
    showAttachment: function () {
      return !this.showTextBlock && !this.showMeeting
    },
    isGordanAndBetty: function () {
      return this.$route.path.includes('gordon-and-betty')
    },
    showTextBlock: function () {
      return (!this.hasAttachments || !this.imageURL) && !this.showMeeting
    },
    showMeeting: function () {
      return this.firstAttachment && this.firstAttachment.type === this.TypeMeetingAttachment
    },
    firstAttachment: function () {
      return this.hasAttachments ? this.post.attachments[0] : null
    },
    imageOffset: function () {
      if (!this.hasAttachments) {
        return null
      }

      const offsets = this.post.attachments.map((attachment, offset) => {
        if (attachment.type === 'image') {
          return offset
        }

        if ((attachment.type === 'link' || attachment.type === 'video' || attachment.type === 'embeddedVideo' || attachment.type === 'iframe') && (attachment.metadata.ogImage || attachment.metadata.screenshot)) {
          return offset
        }

        if (attachment.type === 'whiteboard') {
          return offset
        }
      }).filter((offsets) => offsets >= 0)

      return offsets.length > 0 ? offsets[0] : null
    },
    imageAttachment: function () {
      const offset = this.imageOffset
      return offset >= 0 ? this.post.attachments[offset] || null : null
    },
    imageURL: function () {
      switch (this.imageAttachmentType) {
        case 'link':
        case 'video':
        case 'iframe':
        case 'embeddedVideo':
          if (this.imageAttachment.metadata.ogImage) {
            return this.imageAttachment.metadata.ogImage
          } else {
            return this.imageAttachment.metadata.screenshot
          }
        case 'image':
        case 'whiteboard':
          return this.imageAttachment.url
      }
      if (this.videoAttachmentType) {
        return this.firstAttachment.url + `?t=${new Date().getTime()}`
      }
    },
    isFirstAttachmentVideoProcessing: function () {
      if (this.videoAttachmentType && this.firstAttachment.metadata.transactionId) {
        return this.$store.getters.isAttachmentVideoProcessing(this.firstAttachment.metadata.transactionId)
      }

      return false
    },
    imageBackground: function () {
      const style = {}

      if (this.imageURL && this.useProxy) {
        style.backgroundImage = `url('${imageProxy.grid(this.imageURL)}')`
      } else {
        style.backgroundImage = `url('${this.imageURL}')`
      }

      return style
    },
    imageAttachmentType: function () {
      return this.imageAttachment ? this.imageAttachment.type : null
    },
    videoAttachmentType: function () {
      return this.firstAttachment && (this.firstAttachment.type === 'video' || this.firstAttachment.type === 'embeddedVideo')
    },
    isWhiteboard: function () {
      return this.imageAttachmentType === 'whiteboard'
    },
    attachmentIcon: function () {
      if (!this.hasAttachments) {
        return null
      }

      if (this.firstAttachment.type === 'meeting') {
        return 'fal fa-calendar-alt'
      }

      if (this.post.attachments.length > 1) {
        return 'fal fa-clone'
      }

      if (this.firstAttachment.type === 'link') {
        return 'fal fa-link'
      }

      if (this.firstAttachment.type === 'iframe') {
        return 'fal fa-browser'
      }

      if (this.videoAttachmentType) {
        return 'fal fa-video'
      }

      if (this.firstAttachment.type === 'poll') {
        return 'fal fa-poll-h'
      }

      if (this.firstAttachment.type === 'file') {
        const icon = fileType.iconForNameOrType(this.firstAttachment.metadata.name, this.firstAttachment.metadata.type)
        return `${icon.class}`.replace('far', 'fal')
      }

      return null
    },
    text: function () {
      if (this.post.body) {
        return this.post.body
      }

      if (this.hasAttachments) {
        if (this.firstAttachment.type === 'file') {
          return this.firstAttachment.metadata.name
        }

        if (this.firstAttachment.type === 'link' || this.firstAttachment.type === 'iframe' || this.videoAttachmentType) {
          return this.firstAttachment.metadata.ogTitle || this.firstAttachment.metadata.title
        }
      }
    },
    swooshTextSizeFactor: function () {
      const textSize = this.post.postFeatures.name.length || 0
      return textSize > 20 ? 0.8 : 1
    },
    cssVars: function () {
      return {
        '--swooshTextSizeFactor': this.swooshTextSizeFactor
      }
    },
    isRecommendation: function () {
      return this.post && this.post.recommended
    },
    useProxy: function () {
      return this.$route.name !== 'iseffinalistbooth'
    },
    pollUserAnswerSummary: function () {
      var summary = this.$store.getters.getPollUserOptionSummary(this.post.id)
      if (summary) return `${summary.completed}/${summary.total}`
      return ''
    },
    showPollAnswerSummary: function () {
      return this.post && this.post.project && this.post.project.displayType === 'registrationForm' && this.$store.getters.getPollUserOptionSummary(this.post.id)
    }
  },
  watch: {
    tourStatus: function (value, oldValue) {
      if (this.preloadForOnboarding) {
        if (value === this.JudgeOnboardingStep0) {
          this.$emit('showPost', this.post.id, true)
          this.$emit('toggleAvailability', false)
        }

        if (value === this.JudgeOnboardingStep1 || value === this.JudgeOnboardingStep2) {
          this.$emit('toggleAvailability', true)
        }
      }
    }
  },
  methods: {
    toggleModal: function () {
      this.$emit('showPost', this.post.id, true)
    },
    thumbnail: imageProxy.thumbnail
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

::v-deep .swoosh {
  bottom: 0;
  width: 100%;
  height: 46% !important;
  clip-path: path('M 0 57 C 108 101 135 21 250 57 L 250 150 L 0 150 Z');
  &-text {
    font-size: calc(1.1rem * var(--swooshTextSizeFactor));
  }
  @include mobile {
    bottom: 0%;
    height: 48% !important;
    &-text-container {
      height: 38%;
    }
    &-text {
      font-size: calc(1rem * var(--swooshTextSizeFactor));
      line-height: 0.9rem;
    }
  }
  @media (max-width: 670px) {
    height: 52% !important;
    &-text-container {
      height: 32%;
    }
  }
  @media (max-width: 630px) {
    clip-path: path('M 0 61 C 84 100 105 20 200 61 L 200 149 L 0 150 Z');
    height: 54% !important;
    &-text {
      font-size: calc(0.7rem * var(--swooshTextSizeFactor));
      line-height: 0.7rem;
    }
  }
  @media (max-width: 560px) {
    clip-path: path('M 0 61 C 65 95 108 31 178 65 L 178 150 L 0 150 Z');
    height: 62% !important;
    &-text-container {
      height: 32%;
    }
  }
  @media (max-width: 500px) {
    clip-path: path('M 0 61 C 65 95 108 31 178 65 L 178 150 L 0 150 Z');
    height: 64% !important;
    &-text-container {
      height: 30%;
    }
  }
  @media (max-width: 460px) {
    clip-path: path('M 0 73 C 70 92 90 46 156 74 L 157 150 L 0 150 Z');
    height: 74% !important;
    &-text-container {
      height: 24%;
    }
    &-text {
      font-size: calc(0.5rem * var(--swooshTextSizeFactor));
      line-height: 0.4rem;
    }
  }
  @media (max-width: 420px) {
    clip-path: path('M 0 73 C 64 93 72 55 137 73 L 138 149 L 0 150 Z');
    height: 85% !important;
    &-text-container {
      height: 24%;
    }
  }
  @media (max-width: 380px) {
    clip-path: path('M 0 73 C 64 93 72 55 137 73 L 138 149 L 0 150 Z');
    height: 90% !important;
    &-text-container {
      height: 22%;
    }
  }
}
.markdown-span-override h1{
  font-size: 1rem;
}

.column {
  height: calc(800px/3);
  padding: 0.5rem;
  @include mobile {
    height: 33vw;
    padding: 0.09rem;
  }
}

.content{
  cursor: pointer;
  position:relative;
  height:100%;

  border: 2px solid whitesmoke;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  background: white;

  &.selected {
    box-shadow: 3px 3px 5px rgba(10, 10, 10, 0.3), 0 0 0 1px $primary;
  }

  div {
    height:100%;
  }

  .user-avatar {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    z-index: 2;
    img {
      width: 3rem;
      height: 3rem;
      max-width: 3rem;
      max-height: 3rem;
      background: white;
      border-radius: 50%;
      box-shadow: $default-shadow;
      @include mobile {
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }

  .image {
    background-size: cover;

    &.is-whiteboard {
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
    }

    &.is-finalistBooth {
     background-size: cover;
     background-repeat: no-repeat;
     background-position-y: 25%;
    }
  }
  .status-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.2rem;
    height: 2.2rem;
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
    z-index: 2;
    background: white;
    color: orange;
    border-radius: 100px;
    font-size: 1rem;
    @include mobile {
      width: 1.3rem;
      height: 1.3rem;
      font-size: 0.6rem;
    }
  }
  .icon {
    width: 3rem;
    height: 3rem;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 2;
    background: black;
    color: white;
    border-radius: 100px;
    opacity: 0.5;
    font-size: 1.5rem;
    @include mobile {
      width: 1.5rem;
      height: 1.5rem;
      font-size: 0.75rem;
    }
    &.bottom {
      bottom: 0.5rem;
      top: unset;
    }
  }
  @include hover-cards-effect
}

.review-icon {
  color: orange;
  font-size: 1.1rem;
  @include mobile {
    font-size: 0.7rem;
  }
}

.completed-icon {
  color: green;
  font-size: 2.3rem;
  @include mobile {
    font-size: 1.4rem;
  }
}

.content h1, .content h2, .content h3, .content h4, .content h5, .content h6 {
  color: #fff;
  margin:0.5rem;
  @include widescreen{
    font-weight: 800;
    font-size: 1.3rem;
    line-height:1.6rem;
  }
  @include desktop-only{
    font-weight: 800;
    font-size: 0.98rem;
    line-height:1.3rem;
  }
  @include tablet-only {
    font-weight: 800;
    font-size: 0.95rem;
    line-height:1.3rem;
  }
  @include mobile {
    font-weight: 800;
    font-size: 0.85rem;
    line-height:1.2rem;
  }
  @include mini-mobile {
    font-size: 0.6rem;
    line-height:0.9rem;
  }
}

.title-text strong, .title-text h1{
  color:#fff;
  strong{
    color:#fff;
  }
}
.gordon-and-betty-booth {
  background-position: right 0% bottom 50% !important;
  background-size: cover !important;
}
.lock-icon {
  left: 8rem;
  @include mobile {
    left: 3rem;
  }
  @include mini-mobile {
    left: 3.8rem;
  }
  @media only screen and (max-width: 378px) {
    left: 3.3rem;
  }
  @media only screen and (width: 428px) {
    left: 4.5rem;
  }
  @media only screen and (width: 412px) {
    left: 4.3rem;
  }
}
</style>
